import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, MapPin, Phone, Navigation, Image, FileText, 
  CheckSquare, MessageSquare, Bot, Play, CheckCircle2,
  Clock, AlertTriangle, Upload, X, Camera, Package,
  Palette, ClipboardCheck, AlertCircle, Loader2, Send, PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CallHistoryTimeline from '../components/CallHistoryTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CrewJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Status action state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhotoType, setSelectedPhotoType] = useState('before');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  
  // Notes state
  const [newNote, setNewNote] = useState('');
  
  // AI Assistant state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      setJob(response.data);
      
      // Initialize default checklist if none exists
      if (!response.data.checklists || response.data.checklists.length === 0) {
        await initializeDefaultChecklist(response.data.id, response.data.job_type);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultChecklist = async (jobId, jobType) => {
    const defaultChecklist = {
      id: `checklist-${Date.now()}`,
      name: `${jobType} Checklist`,
      items: [
        { id: '1', task: 'Site inspection & prep', completed: false, required: true },
        { id: '2', task: 'Take "Before" photos', completed: false, required: true },
        { id: '3', task: 'Set up equipment & safety', completed: false, required: true },
        { id: '4', task: 'Surface preparation', completed: false, required: true },
        { id: '5', task: 'Apply coating/perform work', completed: false, required: true },
        { id: '6', task: 'Quality inspection', completed: false, required: true },
        { id: '7', task: 'Take "After" photos', completed: false, required: true },
        { id: '8', task: 'Clean up & customer walkthrough', completed: false, required: true }
      ]
    };

    try {
      await axios.patch(`${API}/jobs/${jobId}`, {
        checklists: [defaultChecklist]
      });
      fetchJob();
    } catch (error) {
      console.error('Error initializing checklist:', error);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/status`, {
        status: newStatus
      });
      
      toast.success(`Status updated to ${newStatus}`);
      fetchJob();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleChecklistToggle = async (checklistId, itemId, completed) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/checklist`, {
        checklist_id: checklistId,
        item_id: itemId,
        completed: !completed
      });
      
      fetchJob();
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast.error('Failed to update checklist');
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhotoFile) {
      toast.error('Please select a photo');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPhotoFile);
      formData.append('file_type', 'photo');
      formData.append('caption', `${selectedPhotoType}: ${photoCaption}`);

      await axios.post(`${API}/jobs/${jobId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo uploaded successfully');
      setPhotoDialogOpen(false);
      setSelectedPhotoFile(null);
      setPhotoCaption('');
      fetchJob();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAiAssistant = async () => {
    if (!aiQuestion.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/ai-assistant`, {
        query: aiQuestion
      });

      setAiResponse(response.data.response);
      setAiHistory([...aiHistory, {
        question: aiQuestion,
        answer: response.data.response,
        timestamp: new Date()
      }]);
      setAiQuestion('');
    } catch (error) {
      console.error('Error with AI assistant:', error);
      toast.error('AI assistant temporarily unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const openInMaps = () => {
    if (!job) return;
    const address = `${job.address}, ${job.city}, ${job.state} ${job.zip_code}`;
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  const callCustomer = () => {
    if (job?.customer_phone) {
      window.location.href = `tel:${job.customer_phone}`;
    }
  };

  const getStatusActions = () => {
    if (!job) return [];
    
    const { status } = job;
    
    if (status === 'scheduled') {
      return [
        { label: 'Mark Arrived', status: 'in_progress', variant: 'default', icon: Play }
      ];
    } else if (status === 'in_progress') {
      return [
        { label: 'Mark Complete', status: 'completed', variant: 'default', icon: CheckCircle2 }
      ];
    }
    
    return [];
  };

  const calculateChecklistProgress = () => {
    if (!job?.checklists || job.checklists.length === 0) return 0;
    
    let totalItems = 0;
    let completedItems = 0;
    
    job.checklists.forEach(checklist => {
      checklist.items?.forEach(item => {
        totalItems++;
        if (item.completed) completedItems++;
      });
    });
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const getPhotosByType = (type) => {
    if (!job?.photos) return [];
    return job.photos.filter(photo => 
      photo.caption?.toLowerCase().includes(type.toLowerCase())
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-96">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-gray-400" />
          <h3 className="mt-2 text-lg font-semibold">Job not found</h3>
          <Button onClick={() => navigate('/crew')} className="mt-4">
            Back to Portal
          </Button>
        </div>
      </Layout>
    );
  }

  const statusActions = getStatusActions();
  const progress = calculateChecklistProgress();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/crew')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Jobs
          </Button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{job.customer_name}</h1>
              <p className="text-gray-600">{job.job_type}</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge className="text-sm">
                {job.status?.replace('_', ' ').toUpperCase()}
              </Badge>
              {statusActions.map((action) => (
                <Button
                  key={action.status}
                  variant={action.variant}
                  onClick={() => handleStatusUpdate(action.status)}
                  disabled={updatingStatus}
                  className="gap-2"
                >
                  {updatingStatus ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <action.icon className="h-4 w-4" />
                  )}
                  {action.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span>Job Progress</span>
              <span className="font-semibold">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Button
            variant="outline"
            onClick={openInMaps}
            className="w-full justify-start gap-2"
          >
            <Navigation className="h-4 w-4" />
            Open in Maps
          </Button>
          <Button
            variant="outline"
            onClick={callCustomer}
            className="w-full justify-start gap-2"
          >
            <Phone className="h-4 w-4" />
            Call Customer
          </Button>
          <Button
            variant="outline"
            onClick={() => setAiDialogOpen(true)}
            className="w-full justify-start gap-2"
          >
            <Bot className="h-4 w-4" />
            AI Assistant
          </Button>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="checklist">Checklist</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="calls">
              <PhoneCall className="h-4 w-4 mr-1" />
              Calls
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Job Overview Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Job Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="font-semibold">Address:</span>
                  <p className="text-gray-600">
                    {job.address}<br />
                    {job.city}, {job.state} {job.zip_code}
                  </p>
                </div>
                <div>
                  <span className="font-semibold">Contact:</span>
                  <p className="text-gray-600">
                    {job.customer_phone}<br />
                    {job.customer_email}
                  </p>
                </div>
                {job.scheduled_start && (
                  <div>
                    <span className="font-semibold">Scheduled:</span>
                    <p className="text-gray-600">
                      {new Date(job.scheduled_start).toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scope & Products Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Scope of Work
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {job.scope_details || 'No scope details provided'}
                </p>
                {job.surfaces && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h4 className="font-semibold mb-2">Surfaces & Materials:</h4>
                    <pre className="text-sm text-gray-600">
                      {JSON.stringify(job.surfaces, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Special Instructions */}
            {job.special_instructions && (
              <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                    <AlertTriangle className="h-5 w-5" />
                    Special Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-yellow-900 dark:text-yellow-100">
                    {job.special_instructions}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist" className="space-y-4">
            {job.checklists && job.checklists.length > 0 ? (
              job.checklists.map((checklist) => (
                <Card key={checklist.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardCheck className="h-5 w-5" />
                      {checklist.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {checklist.items?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() =>
                            handleChecklistToggle(checklist.id, item.id, item.completed)
                          }
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className={`${item.completed ? 'line-through text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                            {item.task}
                            {item.required && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Required
                              </Badge>
                            )}
                          </p>
                          {item.completed && item.completed_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              Completed {new Date(item.completed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No checklist available
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Job Photos
                  </CardTitle>
                  <Button onClick={() => setPhotoDialogOpen(true)} size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Before Photos */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Before Photos ({getPhotosByType('before').length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {getPhotosByType('before').map((photo, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                        <img
                          src={photo.url}
                          alt={photo.caption}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    ))}
                    {getPhotosByType('before').length === 0 && (
                      <div className="col-span-2 text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                        No before photos yet
                      </div>
                    )}
                  </div>
                </div>

                {/* During Photos */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">During Photos ({getPhotosByType('during').length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {getPhotosByType('during').map((photo, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                        <img
                          src={photo.url}
                          alt={photo.caption}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    ))}
                    {getPhotosByType('during').length === 0 && (
                      <div className="col-span-2 text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                        No during photos yet
                      </div>
                    )}
                  </div>
                </div>

                {/* After Photos */}
                <div>
                  <h4 className="font-semibold mb-3">After Photos ({getPhotosByType('after').length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {getPhotosByType('after').map((photo, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                        <img
                          src={photo.url}
                          alt={photo.caption}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    ))}
                    {getPhotosByType('after').length === 0 && (
                      <div className="col-span-2 text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                        No after photos yet
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Job Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add notes about this job..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="mb-3"
                  rows={4}
                />
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/jobs/${jobId}/notes`, {
                        text: newNote
                      });
                      toast.success('Note added');
                      setNewNote('');
                      fetchJob();
                    } catch (error) {
                      toast.error('Failed to add note');
                    }
                  }}
                  disabled={!newNote.trim()}
                >
                  Add Note
                </Button>

                <div className="mt-6 space-y-3">
                  {job.activity_log && job.activity_log.length > 0 ? (
                    job.activity_log.slice().reverse().map((log, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <p className="text-sm text-gray-600">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                        <p className="font-medium">{log.action}</p>
                        <p className="text-sm text-gray-600">{log.user_name}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">No activity yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Call History Tab - Safe Mode */}
          <TabsContent value="calls" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <PhoneCall className="h-5 w-5" />
                      Call History
                    </CardTitle>
                    <CardDescription>All calls related to this job from CallRail</CardDescription>
                  </div>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">Safe Mode</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <CallHistoryTimeline 
                  jobId={jobId}
                  limit={10}
                  showHeader={false}
                  compact={false}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Photo Upload Dialog */}
        <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Photo</DialogTitle>
              <DialogDescription>
                Upload before, during, or after photos for this job
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Photo Type</label>
                <select
                  value={selectedPhotoType}
                  onChange={(e) => setSelectedPhotoType(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="before">Before</option>
                  <option value="during">During / In Progress</option>
                  <option value="after">After / Completed</option>
                  <option value="issue">Issue / Problem</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Select Photo</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedPhotoFile(e.target.files[0])}
                  capture="environment"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Caption (Optional)</label>
                <Input
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  placeholder="Add a description..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPhotoDialogOpen(false)}
                disabled={uploadingPhoto}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePhotoUpload}
                disabled={uploadingPhoto || !selectedPhotoFile}
              >
                {uploadingPhoto ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Assistant Dialog */}
        <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Crew Assistant
              </DialogTitle>
              <DialogDescription>
                Ask questions about this job, procedures, materials, or safety
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* AI History */}
              {aiHistory.length > 0 && (
                <div className="space-y-3 max-h-60 overflow-y-auto border rounded-lg p-3">
                  {aiHistory.map((item, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="bg-blue-50 dark:bg-blue-950 p-2 rounded">
                        <p className="text-sm font-semibold">Q: {item.question}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <p className="text-sm">{item.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Current Response */}
              {aiResponse && !aiLoading && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
                </div>
              )}

              {/* Question Input */}
              <div className="space-y-2">
                <Textarea
                  placeholder="Ask a question... (e.g., 'What should I do about chalky paint?')"
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAiAssistant();
                    }
                  }}
                />
                <Button
                  onClick={handleAiAssistant}
                  disabled={aiLoading || !aiQuestion.trim()}
                  className="w-full"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Ask AI Assistant
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CrewJobDetail;
